/**
 * The online match driver (BUILD_PLAN section 8): joins the room first (the welcome names the
 * mechs, the seat and the arena), then predicts the own mech ahead of the server with the shared
 * step, reconciles against every authoritative snapshot, draws the opponent a fixed delay behind
 * on the authoritative timeline, and shows damage and flow only once the server confirms them.
 * Prediction ticks run from a timer, not the render loop, so a slow or hidden tab keeps sending
 * inputs. Nothing here decides gameplay: it sends inputs and renders what comes back (#1).
 */
import { botDriver } from '@steelwake/shared/ai';
import type { ArenaRuntime } from '@steelwake/shared/arena';
import {
  hashMatchState,
  otherSlot,
  releasedInput,
  type Combatant,
  type Deployable,
  type MatchEvent,
  type MatchState,
  type Slot,
} from '@steelwake/shared/combat';
import {
  applyStateDelta,
  eventSlot,
  filterEvents,
  NET,
  NetClock,
  Predictor,
  SnapshotMessageSchema,
  TICK_MS,
  Timeline,
  type RoomPhase,
  type StatusMessage,
} from '@steelwake/shared/net';
import type { MechPose, Vec3 } from '@steelwake/shared/sim';

import type {
  DriverContext,
  MatchDescriptor,
  MatchDriver,
  MatchSource,
  MechFrame,
  PrepareContext,
  ProjectileView,
  ViewPose,
} from '../game/match/driver.js';
import type { InputSample } from '../game/sim/input.js';
import type { OnlineHud } from '../hud/types.js';
import { isConditioned, type NetConditions } from './conditioner.js';
import { joinMatchRoom, type JoinedRoom } from './connection.js';
import type { NetMetrics } from './metrics.js';
import { readStored, reconnectStorageKey, writeStored } from './params.js';

export interface OnlineParams {
  roomId: string;
  ticket: string | null;
}

const ZERO: Vec3 = { x: 0, y: 0, z: 0 };
/** Most prediction ticks one pump may run (a stalled tab catches up gradually, never in a burst). */
const MAX_STEPS_PER_PUMP = 10;
/** Input packets per second the client will put on the wire, under the server's 45/s line. */
const SEND_RATE_PER_SECOND = 36;
const SEND_BURST = 8;

function wrap(a: number): number {
  return Math.atan2(Math.sin(a), Math.cos(a));
}

function toView(p: MechPose, offset: Vec3 = ZERO): ViewPose {
  return {
    x: p.position.x + offset.x,
    y: p.position.y + offset.y,
    z: p.position.z + offset.z,
    yaw: p.yaw,
    aimYaw: p.aimYaw,
    aimPitch: p.aimPitch,
  };
}

function blend(a: MechPose, b: MechPose, k: number, offset: Vec3): ViewPose {
  return {
    x: a.position.x + (b.position.x - a.position.x) * k + offset.x,
    y: a.position.y + (b.position.y - a.position.y) * k + offset.y,
    z: a.position.z + (b.position.z - a.position.z) * k + offset.z,
    yaw: a.yaw + wrap(b.yaw - a.yaw) * k,
    aimYaw: a.aimYaw + wrap(b.aimYaw - a.aimYaw) * k,
    aimPitch: a.aimPitch + (b.aimPitch - a.aimPitch) * k,
  };
}

function storage(): Storage | null {
  return typeof sessionStorage === 'undefined' ? null : sessionStorage;
}

/** True for events about the enemy's own actions, shown when the drawn enemy reaches them. */
function isRemoteAction(e: MatchEvent, enemy: Slot, me: Slot): boolean {
  if (eventSlot(e) !== enemy) return false;
  if (e.t === 'hit' || e.t === 'blocked') return e.target !== me;
  if (e.t === 'kill') return e.victim !== me;
  return true;
}

/** An online match named by its room; the ticket (or a held seat's token) opens the seat. */
export class OnlineSource implements MatchSource {
  private link: JoinedRoom | null = null;
  private driver: NetMatchDriver | null = null;
  private cheat: 'speed' | null = null;
  private readonly pendingStatus: StatusMessage[] = [];
  private readonly pendingSnapshots: unknown[] = [];
  private pendingLeave: number | null = null;

  constructor(private readonly params: OnlineParams) {}

  async prepare(signal: AbortSignal, ctx: PrepareContext): Promise<MatchDescriptor> {
    const { url } = ctx;
    // The simulated link and the broken client are tools: never in a production build.
    const devTools = import.meta.env.DEV || url.e2e;
    this.cheat = devTools ? url.cheat : null;
    ctx.onStatus('Connecting to the match server…');
    const link = await joinMatchRoom(
      {
        roomId: this.params.roomId,
        ticket: this.params.ticket,
        reconnectionToken: readStored(storage(), reconnectStorageKey(this.params.roomId)),
        conditions: devTools ? url.net : null,
      },
      {
        onSnapshot: (m) => {
          if (this.driver) this.driver.onSnapshot(m);
          else this.pendingSnapshots.push(m);
        },
        onStatus: (m) => {
          if (this.driver) this.driver.onStatus(m);
          else this.pendingStatus.push(m);
        },
        onLeave: (code) => {
          if (this.driver) this.driver.onLeft(code);
          else this.pendingLeave = code;
        },
        onDrop: (code) => this.driver?.onDropped(code),
        onReconnect: () => this.driver?.onReconnected(),
      },
      signal,
    );
    this.link = link;
    const giveUpSeat = () => {
      link.unsubscribe();
      void link.room.leave(true);
    };
    if (signal.aborted) {
      giveUpSeat();
      signal.throwIfAborted();
    }
    // Torn down while the assets load (a navigation away): nobody will drive the seat.
    signal.addEventListener(
      'abort',
      () => {
        if (!this.driver) giveUpSeat();
      },
      { once: true },
    );
    writeStored(storage(), reconnectStorageKey(this.params.roomId), link.reconnectionToken);
    return {
      mechs: link.welcome.setup.mechs,
      mySlot: link.welcome.slot,
      arenaId: link.welcome.setup.arenaId,
      arenaHash: link.welcome.arenaHash,
      loadouts: link.welcome.setup.loadouts ?? [null, null],
      balanceVersion: link.welcome.setup.balance?.version ?? 0,
    };
  }

  createDriver(ctx: DriverContext): MatchDriver {
    const link = this.link;
    if (!link) throw new Error('prepare() has not resolved');
    const driver = new NetMatchDriver(link, ctx, { roomId: this.params.roomId, cheat: this.cheat });
    this.driver = driver;
    for (const s of this.pendingStatus.splice(0)) driver.onStatus(s);
    for (const s of this.pendingSnapshots.splice(0)) driver.onSnapshot(s);
    if (this.pendingLeave !== null) driver.onLeft(this.pendingLeave);
    else driver.ready();
    return driver;
  }
}

export interface NetDriverOptions {
  roomId: string;
  cheat: 'speed' | null;
}

export class NetMatchDriver implements MatchDriver {
  readonly mySlot: Slot;
  readonly enemySlot: Slot;
  readonly practice = null;

  private readonly link: JoinedRoom;
  private readonly roomId: string;
  private readonly cheat: 'speed' | null;
  private readonly callsigns: [string, string];
  private readonly predictor: Predictor;
  private readonly timeline = new Timeline();
  private readonly clock = new NetClock();
  private latestAuth: MatchState;
  private hud: MatchState;
  private prevPredicted: MatchState;
  private acc = 0;
  private alpha = 0;
  private offset: Vec3 = ZERO;
  private phase: RoomPhase = 'waiting';
  private lastStatus: StatusMessage | null = null;
  private connectionLost = false;
  private lastSample: InputSample | null = null;
  /** Events waiting for the next frame: own predicted actions and server facts about us. */
  private readonly queuedEvents: MatchEvent[] = [];
  /** The enemy's actions, released when the drawn enemy reaches their tick. */
  private readonly scheduled: { tick: number; event: MatchEvent }[] = [];
  private lastResyncMs = -Infinity;
  private resyncPending = false;
  private now = 0;
  private lastPumpMs: number | null = null;
  private timer: ReturnType<typeof setInterval> | null;
  private readonly counters = {
    desyncs: 0,
    resyncs: 0,
    snapshots: 0,
    fullSnapshots: 0,
    badMessages: 0,
    late: 0,
    pumps: 0,
    steps: 0,
    sent: 0,
    jumps: 0,
    snapshotMs: 0,
    snapshotMaxMs: 0,
    pumpMs: 0,
    pumpMaxMs: 0,
  };
  /** The newest tick an input packet was sent for; a re-anchor never re-sends earlier ticks. */
  private lastSentTick = 0;
  private sendTokens = SEND_BURST;
  private sendTokenAt: number | null = null;

  constructor(link: JoinedRoom, ctx: DriverContext, options: NetDriverOptions) {
    this.link = link;
    this.roomId = options.roomId;
    this.cheat = options.cheat;
    this.mySlot = link.welcome.slot;
    this.enemySlot = otherSlot(this.mySlot);
    const names = link.welcome.players.map((p) => p.callsign);
    this.callsigns = [names[0] ?? 'Pilot 1', names[1] ?? 'Pilot 2'];
    this.predictor = new Predictor({
      setup: link.welcome.setup,
      arena: ctx.arena,
      world: ctx.world,
      bot: botDriver,
      slot: this.mySlot,
      // Predicted shots resolve against the enemy as drawn, like the server's rewind does.
      enemyPoseAt: () => this.timeline.pose(this.enemySlot, this.clock.renderTick(this.now)),
    });
    this.latestAuth = this.predictor.predicted;
    this.hud = this.latestAuth;
    this.prevPredicted = this.latestAuth;
    // The handshake already measured the link: the first inputs lead by the right amount.
    this.clock.seedRtt(link.rttMs);
    this.timer = setInterval(() => this.pump(performance.now()), TICK_MS);
  }

  /** Assets are loaded; the server may start the countdown. */
  ready(): void {
    this.link.send('ready', { t: 'ready' });
  }

  onStatus(msg: StatusMessage): void {
    this.lastStatus = msg;
    if (msg.phase === 'live' && this.phase !== 'live') {
      // A fresh timeline after the start or a resume: the pause shifted the server's clock.
      this.clock.reanchor();
      this.acc = 0;
      this.lastPumpMs = null;
    }
    this.phase = msg.phase;
    if (msg.phase === 'ended' || msg.phase === 'aborted') {
      writeStored(storage(), reconnectStorageKey(this.roomId), null);
    }
  }

  onLeft(code: number): void {
    if (this.phase === 'waiting' || this.phase === 'live' || this.phase === 'paused') {
      this.phase = 'ended';
      this.lastStatus = {
        t: 'status',
        phase: 'ended',
        slot: null,
        secondsLeft: null,
        reason: `connection closed (${String(code)})`,
      };
    }
    this.connectionLost = false;
  }

  onDropped(_code: number): void {
    this.connectionLost = true;
  }

  onReconnected(): void {
    this.connectionLost = false;
    // The server sends a full snapshot and a live status once the seat is back.
    this.clock.reanchor();
    this.lastPumpMs = null;
  }

  onSnapshot(raw: unknown): void {
    const startedMs = performance.now();
    this.handleSnapshot(raw);
    const cost = performance.now() - startedMs;
    this.counters.snapshotMs += cost;
    if (cost > this.counters.snapshotMaxMs) this.counters.snapshotMaxMs = cost;
  }

  private handleSnapshot(raw: unknown): void {
    const parsed = SnapshotMessageSchema.safeParse(raw);
    if (!parsed.success) {
      this.counters.badMessages++;
      return;
    }
    const msg = parsed.data;
    const now = performance.now();
    this.now = now;
    let auth: MatchState;
    if (msg.full) {
      auth = msg.full;
      this.counters.fullSnapshots++;
      this.resyncPending = false;
    } else {
      const base = this.timeline.latest();
      if (this.resyncPending || !base || base.tick !== msg.base || msg.delta === undefined) {
        // Off the chain (a missed tick, or waiting for the full state already asked for).
        this.requestResync('base', now);
        return;
      }
      auth = applyStateDelta(base, msg.delta, msg.events);
    }
    if (msg.hash !== null && hashMatchState(auth) !== msg.hash) {
      this.counters.desyncs++;
      this.requestResync('hash', now);
      return;
    }
    this.counters.snapshots++;
    const late = msg.late[this.mySlot];
    const echo = msg.echoMs[this.mySlot];
    this.counters.late += late;
    this.latestAuth = auth;
    this.timeline.push(auth);
    this.clock.onSnapshot(msg.tick, now, echo !== null ? Math.max(0, now - echo) : null, late);
    for (const e of filterEvents(msg.events, this.mySlot, 'server')) {
      if (isRemoteAction(e, this.enemySlot, this.mySlot)) {
        this.scheduled.push({ tick: msg.tick, event: e });
      } else {
        this.queuedEvents.push(e);
      }
    }
    if (this.phase === 'live') {
      if (this.predictor.reconcile(auth, msg.events)) {
        const c = this.predictor.takeCorrection();
        if (c) this.absorb(c);
      } else {
        this.jump(now);
      }
    } else {
      this.predictor.reset(auth);
      this.prevPredicted = auth;
    }
    this.hud = this.mergeHud(auth, this.predictor.predicted);
  }

  /** Stores the frame's input sample, runs any due prediction ticks, returns the events to show. */
  advance(frameMs: number, sample: InputSample): MatchEvent[] {
    this.lastSample = sample;
    this.pump(performance.now());
    const events = this.queuedEvents.splice(0);
    // The enemy's actions arrive early; show them when the drawn enemy gets there.
    if (this.scheduled.length > 0) {
      const renderTick = this.clock.isAnchored ? this.clock.renderTick(this.now) : Infinity;
      let n = 0;
      while (n < this.scheduled.length && this.scheduled[n]!.tick <= renderTick) n++;
      for (const s of this.scheduled.splice(0, n)) events.push(s.event);
    }
    if (this.offset !== ZERO) {
      const k = Math.exp((-Math.LN2 * frameMs) / NET.CORRECTION_HALF_LIFE_MS);
      const o = { x: this.offset.x * k, y: this.offset.y * k, z: this.offset.z * k };
      this.offset = Math.hypot(o.x, o.y, o.z) < 1e-3 ? ZERO : o;
    }
    return events;
  }

  get state(): MatchState {
    return this.hud;
  }

  get ownState(): MatchState {
    return this.predictor.predicted;
  }

  mech(slot: Slot): MechFrame {
    if (slot === this.mySlot) {
      const a = this.prevPredicted.mechs[slot].body;
      const b = this.predictor.predicted.mechs[slot].body;
      const jump =
        Math.hypot(b.position.x - a.position.x, b.position.z - a.position.z) > 8 ||
        this.prevPredicted.flow.round !== this.predictor.predicted.flow.round;
      return {
        pose: blend(a, b, jump ? 1 : this.alpha, this.offset),
        combatant: this.hud.mechs[slot],
      };
    }
    const rt = this.clock.renderTick(this.now);
    const pose = this.timeline.pose(slot, rt) ?? this.latestAuth.mechs[slot].body;
    const state = this.timeline.stateAt(rt) ?? this.latestAuth;
    return { pose: toView(pose), combatant: state.mechs[slot] };
  }

  projectiles(): ProjectileView[] {
    const k = this.alpha;
    const prevById = new Map(this.prevPredicted.projectiles.map((p) => [p.id, p]));
    const own: ProjectileView[] = [];
    for (const p of this.predictor.predicted.projectiles) {
      if (p.slot !== this.mySlot) continue;
      const q = prevById.get(p.id);
      own.push(
        q
          ? {
              x: q.position.x + (p.position.x - q.position.x) * k,
              y: q.position.y + (p.position.y - q.position.y) * k,
              z: q.position.z + (p.position.z - q.position.z) * k,
              weapon: p.weapon,
            }
          : { x: p.position.x, y: p.position.y, z: p.position.z, weapon: p.weapon },
      );
    }
    const rt = this.clock.renderTick(this.now);
    const remote = this.timeline.projectiles(this.enemySlot, rt).map((p) => ({
      x: p.position.x,
      y: p.position.y,
      z: p.position.z,
      weapon: p.weapon,
    }));
    return [...own, ...remote];
  }

  features(): ArenaRuntime {
    return this.state.features;
  }

  viewTick(): number {
    return this.predictor.tick - 1 + this.alpha;
  }

  deployables(): readonly Deployable[] {
    return this.predictor.predicted.deployables;
  }

  get finished(): boolean {
    return (
      this.latestAuth.flow.phase === 'matchEnd' ||
      this.phase === 'ended' ||
      this.phase === 'aborted'
    );
  }

  net(): NetMetrics {
    return {
      phase: this.phase,
      status: this.lastStatus,
      rttMs: this.clock.rttMs,
      jitterMs: this.clock.jitterMs,
      lead: this.clock.leadTicks(),
      late: this.counters.late,
      corrections: { ...this.predictor.metrics },
      desyncs: this.counters.desyncs,
      resyncs: this.counters.resyncs,
      snapshots: this.counters.snapshots,
      fullSnapshots: this.counters.fullSnapshots,
      badMessages: this.counters.badMessages,
      pumps: this.counters.pumps,
      steps: this.counters.steps,
      sent: this.counters.sent,
      jumps: this.counters.jumps,
      profile: {
        snapshotMs: this.counters.snapshotMs,
        snapshotMaxMs: this.counters.snapshotMaxMs,
        pumpMs: this.counters.pumpMs,
        pumpMaxMs: this.counters.pumpMaxMs,
      },
      tick: {
        predicted: this.predictor.tick,
        authoritative: this.latestAuth.tick,
        target: this.clock.isAnchored ? this.clock.targetPredictedTick(this.now) : 0,
        render: this.clock.isAnchored ? this.clock.renderTick(this.now) : 0,
      },
    };
  }

  online(): OnlineHud {
    const conditions = this.link.conditioner.current;
    const status = this.lastStatus;
    return {
      matchId: this.link.welcome.matchId,
      phase: this.phase,
      slot: status?.slot ?? null,
      secondsLeft: status?.secondsLeft ?? null,
      reason: status?.reason ?? null,
      connectionLost: this.connectionLost,
      rttMs: this.clock.rttMs,
      lead: this.clock.leadTicks(),
      late: this.counters.late,
      corrections: this.predictor.metrics.count,
      maxCorrectionM: this.predictor.metrics.max,
      desyncs: this.counters.desyncs,
      conditions: isConditioned(conditions) ? { ...conditions } : null,
      callsigns: this.callsigns,
    };
  }

  setNetConditions(conditions: NetConditions): void {
    this.link.conditioner.set(conditions);
  }

  dispose(): void {
    if (this.timer !== null) clearInterval(this.timer);
    this.timer = null;
    this.link.unsubscribe();
    // Leaving on purpose (navigating away) forfeits; a reload or a drop is not consented.
    writeStored(storage(), reconnectStorageKey(this.roomId), null);
    void this.link.room.leave(true);
  }

  // --- internals ---

  /** Runs the prediction ticks that the net clock says are due. */
  private pump(now: number): void {
    this.counters.pumps++;
    this.runPump(now);
    const cost = performance.now() - now;
    this.counters.pumpMs += cost;
    if (cost > this.counters.pumpMaxMs) this.counters.pumpMaxMs = cost;
  }

  private runPump(now: number): void {
    this.now = now;
    if (this.phase !== 'live' || !this.clock.isAnchored || this.connectionLost) {
      this.lastPumpMs = null;
      return;
    }
    const elapsed = this.lastPumpMs === null ? 0 : now - this.lastPumpMs;
    this.lastPumpMs = now;
    if (this.predictor.tick <= this.latestAuth.tick) {
      this.jump(now);
      return;
    }
    const target = this.clock.targetPredictedTick(now);
    this.acc += Math.min(elapsed, 250) * this.clock.rateScale(this.predictor.tick, now);
    let steps = 0;
    while (this.acc >= TICK_MS && steps < MAX_STEPS_PER_PUMP) {
      this.acc -= TICK_MS;
      // Ahead of the target: let the server catch up rather than run further ahead.
      if (this.predictor.tick > target + 2) continue;
      this.queuedEvents.push(...this.predictOne(now));
      steps++;
    }
    if (this.acc > TICK_MS) this.acc = TICK_MS;
    if (this.predictor.tick < target - NET.MAX_LEAD_TICKS) this.jump(now);
    this.alpha = Math.min(1, this.acc / TICK_MS);
  }

  /** Asks for a full state; deltas are ignored until it arrives. Re-asks at most once a second. */
  private requestResync(reason: 'base' | 'hash', now: number): void {
    this.resyncPending = true;
    if (now - this.lastResyncMs < 1000) return;
    this.lastResyncMs = now;
    this.counters.resyncs++;
    this.link.send('resync', { t: 'resync', reason });
  }

  /** Re-anchors the prediction on the newest truth and runs it back up to the target tick. */
  private jump(now: number): void {
    this.counters.jumps++;
    this.predictor.reset(this.latestAuth);
    this.prevPredicted = this.latestAuth;
    this.offset = ZERO;
    this.acc = 0;
    const target = this.clock.targetPredictedTick(now);
    let n = 0;
    while (this.predictor.tick < target && n++ < NET.MAX_LEAD_TICKS + 2) {
      this.queuedEvents.push(...this.predictOne(now));
    }
  }

  private predictOne(now: number): MatchEvent[] {
    let sample: InputSample;
    if (this.lastSample) {
      sample = this.lastSample;
    } else {
      const { seq: _seq, ...rest } = releasedInput(
        this.predictor.predicted.mechs[this.mySlot].lastInput,
      );
      sample = rest;
    }
    this.prevPredicted = this.predictor.predicted;
    const events = this.predictor.predict(sample);
    this.counters.steps++;
    const tick = this.predictor.tick;
    const input = this.predictor.inputFor(tick);
    if (this.cheat === 'speed') this.cheatSpeed();
    // A re-anchor re-predicts ticks whose inputs already went out; the server keeps those. A
    // tab that stalled and is catching up must not burst either: the server reads more than
    // 45 packets a second as a flood, so a token bucket keeps the wire under it and the server
    // holds the last input for the ticks it never hears about.
    if (input && tick > this.lastSentTick && this.takeSendToken(now)) {
      this.lastSentTick = tick;
      this.counters.sent++;
      this.link.send('input', {
        t: 'input',
        tick,
        viewTick: Math.max(0, this.clock.renderTick(now)),
        sentMs: now,
        // The broken client claims three times the forward axis (a value no honest client can
        // produce); the server refuses the packet and flags it.
        input: this.cheat === 'speed' ? { ...input, moveY: input.moveY * 3 } : input,
      });
    }
    return filterEvents(events, this.mySlot, 'predicted');
  }

  /** Refills at SEND_RATE_PER_SECOND with a small burst allowance; false means "skip this one". */
  private takeSendToken(now: number): boolean {
    const elapsed = this.sendTokenAt === null ? 0 : now - this.sendTokenAt;
    this.sendTokenAt = now;
    this.sendTokens = Math.min(
      SEND_BURST,
      this.sendTokens + (elapsed / 1000) * SEND_RATE_PER_SECOND,
    );
    if (this.sendTokens < 1) return false;
    this.sendTokens -= 1;
    return true;
  }

  /** The broken client also moves itself three times as far as the rules allow. */
  private cheatSpeed(): void {
    const before = this.prevPredicted.mechs[this.mySlot].body.position;
    const body = this.predictor.predicted.mechs[this.mySlot].body;
    body.position = {
      x: body.position.x + (body.position.x - before.x) * 2,
      y: body.position.y,
      z: body.position.z + (body.position.z - before.z) * 2,
    };
  }

  private absorb(c: Vec3): void {
    if (Math.hypot(c.x, c.y, c.z) > NET.SNAP_CORRECTION_M) {
      this.offset = ZERO;
      return;
    }
    this.offset = { x: this.offset.x + c.x, y: this.offset.y + c.y, z: this.offset.z + c.z };
  }

  /**
   * The HUD state: the truth, with the own mech's weapons, heat and abilities as predicted so
   * the ammo counter and heat bar answer the trigger at once. Positions and the applied input
   * stay authoritative: `match()` on the debug API is what the server decided.
   */
  private mergeHud(auth: MatchState, predicted: MatchState): MatchState {
    const my = this.mySlot;
    const a = auth.mechs[my];
    const p = predicted.mechs[my];
    const own: Combatant = {
      ...a,
      weapons: p.weapons,
      heat: p.heat,
      overheatTicks: p.overheatTicks,
      venting: p.venting,
      abilities: p.abilities,
      melee: p.melee,
    };
    const mechs: [Combatant, Combatant] = my === 0 ? [own, auth.mechs[1]] : [auth.mechs[0], own];
    return { ...auth, mechs };
  }
}
